import {
  formatPricingFromMinAndMax,
  getAverageFromMinAndMax,
} from '@sly/core/helpers/numbers';
import { ChatFill, Favorite, Flag, HomeLarge, Money } from 'sly/icons';
import replaceShortcodes from 'sly/lib/helpers/replaceShortcodes';
import round from 'sly/lib/helpers/round';

export const getPartnerData =
  (partnerCollection, communityName = '') =>
  (cmsData) => {
    const {
      url,
      id,
      name,
      info: {
        communityPartnerCollectionStats: {
          communityCount,
          typeOfCares,
          priceRangeTOC,
          totalPriceRange,
        } = {},
        ratingStats: { numReviews, reviewsValue: rawReviewsValue } = {},
      } = {},
    } = partnerCollection || {};

    const filteredNonZeroPriceRangeToc = Object.entries(priceRangeTOC).reduce(
      (acc, [key, price]) => {
        if (!!price?.max || !!price?.min) {
          acc[key] = price;
        }
        return acc;
      },
      {}
    );

    const reviewsValue = rawReviewsValue
      ? round(rawReviewsValue, 1)
      : rawReviewsValue;

    const averageCost = getAverageFromMinAndMax(
      totalPriceRange?.min,
      totalPriceRange?.max
    );

    const {
      faqs = [],
      providerName,
      similarProviderNearby,
      popularCommunities,
      yearEstablished,
      logo,
    } = cmsData || {};

    const shortCodes = [
      { regex: /\[reviewsValue\]/g, value: reviewsValue },
      { regex: /\[communityCount\]/g, value: communityCount },
      { regex: /\[numReviews\]/g, value: numReviews },
      {
        regex: /\[ageOfProvider\]/g,
        value: new Date().getFullYear() - yearEstablished,
      },
      {
        regex: /\[yearEstablished\]/g,
        value: yearEstablished,
      },
      {
        regex: /\[averageCost\]/g,
        value: averageCost || '(not available, unfortunately)',
      },
      { regex: /\[providerName\]/g, value: providerName || name },
    ];

    if (communityName) {
      shortCodes.push({ regex: /\[communityName\]/g, value: communityName });
    }

    const parseShortcodes = replaceShortcodes(shortCodes);

    const faqsWithShortcodes = faqs.map(({ description, title, ...rest }) => {
      return {
        description: parseShortcodes(description),
        title: parseShortcodes(title),
        ...rest,
      };
    });

    const description = cmsData?.description
      ? parseShortcodes(cmsData?.description)
      : '';

    const descriptionInCommunityProfile = cmsData?.descriptionInCommunityProfile
      ? parseShortcodes(cmsData?.descriptionInCommunityProfile)
      : '';

    const totalPriceString =
      totalPriceRange &&
      formatPricingFromMinAndMax(totalPriceRange.min, totalPriceRange.max);

    let highlights = [];

    if (yearEstablished) {
      highlights.push({
        text: `Est. ${yearEstablished}`,
        Icon: Flag,
        tooltip: null,
      });
    }
    if (communityCount) {
      highlights.push({
        text: `${communityCount} properties`,
        Icon: HomeLarge,
        tooltip: `The number of this provider's communities that you can explore here on Seniorly.com`,
      });
    }

    if (!communityName && totalPriceRange?.min) {
      highlights.push({
        text: totalPriceString,
        Icon: Money,
        tooltip: null,
      });
    }

    if (reviewsValue) {
      highlights.push({
        text: `Average rating ${reviewsValue * 2.0}`,
        Icon: Favorite,
        tooltip: null,
      });
    }

    if (!communityName && numReviews) {
      highlights.push({
        text: `${numReviews} total reviews`,
        Icon: ChatFill,
        tooltip: null,
      });
    }

    return {
      highlights,
      url,
      id,
      numReviews,
      reviewsValue,
      communityCount,
      description,
      descriptionInCommunityProfile,
      typeOfCares,
      priceRangeTOC: filteredNonZeroPriceRangeToc,
      isPricingTableVisible: !!Object.keys(filteredNonZeroPriceRangeToc).length,
      faqs: faqsWithShortcodes,
      providerName: providerName || name,
      similarProviderNearby,
      popularCommunities,
      logo,
    };
  };
